// ===========================================
// ADMIN TOAST PROVIDER
// ===========================================

import { useCallback, useMemo, useState, type ReactNode } from 'react';
import { clsx } from 'clsx';
import { X, CheckCircle, XCircle, Info } from 'lucide-react';
import { ToastContext } from './ToastContext';

export type ToastVariant = 'success' | 'error' | 'info';

export interface ToastMessage {
  id: string;
  title: string;
  description?: string;
  variant?: ToastVariant;
}

interface ToastProviderProps {
  children: ReactNode;
}

/**
 * Toast notification provider for admin dashboard
 * Provides a context for showing toast notifications
 */
export function ToastProvider({ children }: ToastProviderProps) {
  const [toasts, setToasts] = useState<ToastMessage[]>([]);

  const notify = useCallback((toast: Omit<ToastMessage, 'id'>) => {
    const id = `${Date.now()}-${Math.random()}`;
    setToasts((prev) => [...prev, { ...toast, id }]);

    // Auto-dismiss after 5 seconds
    setTimeout(() => {
      setToasts((prev) => prev.filter((t) => t.id !== id));
    }, 5000);
  }, []);

  const removeToast = useCallback((id: string) => {
    setToasts((prev) => prev.filter((item) => item.id !== id));
  }, []);

  const value = useMemo(() => ({ notify }), [notify]);

  return (
    <ToastContext.Provider value={value}>
      {children}
      <div className="fixed right-6 top-6 z-50 flex w-full max-w-sm flex-col gap-3">
        {toasts.map((toast) => (
          <ToastItem key={toast.id} toast={toast} onClose={removeToast} />
        ))}
      </div>
    </ToastContext.Provider>
  );
}

/**
 * Individual toast item component
 */
function ToastItem({
  toast,
  onClose,
}: {
  toast: ToastMessage;
  onClose: (id: string) => void;
}) {
  const Icon =
    toast.variant === 'success'
      ? CheckCircle
      : toast.variant === 'error'
      ? XCircle
      : Info;

  return (
    <div
      className={clsx(
        'rounded-2xl border bg-white px-4 py-3 shadow-soft animate-in slide-in-from-right',
        toast.variant === 'success' && 'border-emerald-200',
        toast.variant === 'error' && 'border-red-200',
        toast.variant === 'info' && 'border-blue-200',
        !toast.variant && 'border-slate-200'
      )}
    >
      <div className="flex items-start gap-3">
        <Icon
          className={clsx(
            'h-5 w-5 flex-shrink-0',
            toast.variant === 'success' && 'text-emerald-500',
            toast.variant === 'error' && 'text-red-500',
            toast.variant === 'info' && 'text-blue-500'
          )}
        />
        <div className="flex-1">
          <p className="text-sm font-semibold text-slate-900">{toast.title}</p>
          {toast.description && (
            <p className="mt-0.5 text-xs text-slate-500">{toast.description}</p>
          )}
        </div>
        <button
          className="rounded-lg p-1 text-slate-400 hover:text-slate-600 transition"
          onClick={() => onClose(toast.id)}
          aria-label="Dismiss"
        >
          <X className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
}
